import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import PlaceholderPage from "@/pages/PlaceholderPage";
import NewsListPage from "@/pages/NewsListPage";
import ArticleDetailPage from "@/pages/ArticleDetailPage";
import ProjectsListPage from "@/pages/ProjectsListPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import CompaniesListPage from "@/pages/CompaniesListPage";
import CompanyDetailPage from "@/pages/CompanyDetailPage";
import RegionsListPage from "@/pages/RegionsListPage";
import RegionDetailPage from "@/pages/RegionDetailPage";
import CommoditiesListPage from "@/pages/CommoditiesListPage";
import CommodityDetailPage from "@/pages/CommodityDetailPage";
import PolicyTrackerPage from "@/pages/PolicyTrackerPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminFilingsPage from "@/pages/AdminFilingsPage";
import AdminDraftsPage from "@/pages/AdminDraftsPage";
import AdminDraftEditorPage from "@/pages/AdminDraftEditorPage";
import AdminNewsletterPage from "@/pages/AdminNewsletterPage";
import AdminPressReleasesPage from "@/pages/AdminPressReleasesPage";
import AdminAgentKeysPage from "@/pages/AdminAgentKeysPage";
import AdminSubmissionsPage from "@/pages/AdminSubmissionsPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CommodityTicker from "@/components/CommodityTicker";

function Shell({ children }) {
  return (
    <div
      className="min-h-screen flex flex-col bg-bb-offwhite"
      data-testid="app-shell"
    >
      <Navbar />
      <CommodityTicker />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback (session_id in URL fragment) BEFORE any route match
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin/filings" element={<ProtectedRoute><AdminFilingsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDraftsPage /></ProtectedRoute>} />
      <Route path="/admin/drafts" element={<ProtectedRoute><AdminDraftsPage /></ProtectedRoute>} />
      <Route path="/admin/drafts/new" element={<ProtectedRoute><AdminDraftEditorPage /></ProtectedRoute>} />
      <Route path="/admin/drafts/:draftId" element={<ProtectedRoute><AdminDraftEditorPage /></ProtectedRoute>} />
      <Route path="/admin/newsletter" element={<ProtectedRoute><AdminNewsletterPage /></ProtectedRoute>} />
      <Route path="/admin/press-releases" element={<ProtectedRoute><AdminPressReleasesPage /></ProtectedRoute>} />
      <Route path="/admin/agent-keys" element={<ProtectedRoute><AdminAgentKeysPage /></ProtectedRoute>} />
      <Route path="/admin/submissions" element={<ProtectedRoute><AdminSubmissionsPage /></ProtectedRoute>} />
      <Route
        path="/"
        element={
          <Shell>
            <Home />
          </Shell>
        }
      />
        <Route
          path="/news"
          element={
            <Shell>
              <NewsListPage />
            </Shell>
          }
        />
        <Route
          path="/news/:slug"
          element={
            <Shell>
              <ArticleDetailPage />
            </Shell>
          }
        />
        <Route
          path="/projects"
          element={
            <Shell>
              <ProjectsListPage />
            </Shell>
          }
        />
        <Route
          path="/projects/:slug"
          element={
            <Shell>
              <ProjectDetailPage />
            </Shell>
          }
        />
        <Route
          path="/commodities"
          element={
            <Shell>
              <CommoditiesListPage />
            </Shell>
          }
        />
        <Route
          path="/commodities/:slug"
          element={
            <Shell>
              <CommodityDetailPage />
            </Shell>
          }
        />
        <Route
          path="/regions"
          element={
            <Shell>
              <RegionsListPage />
            </Shell>
          }
        />
        <Route
          path="/regions/:slug"
          element={
            <Shell>
              <RegionDetailPage />
            </Shell>
          }
        />
        <Route
          path="/companies"
          element={
            <Shell>
              <CompaniesListPage />
            </Shell>
          }
        />
        <Route
          path="/companies/:slug"
          element={
            <Shell>
              <CompanyDetailPage />
            </Shell>
          }
        />
        <Route
          path="/policy-tracker"
          element={
            <Shell>
              <PolicyTrackerPage />
            </Shell>
          }
        />
        <Route
          path="/data-room"
          element={
            <Shell>
              <PlaceholderPage
                title="Data Room"
                description="Downloadable datasets and source filings — technical reports, EA submissions, royalty schedules, and historical production records."
              />
            </Shell>
          }
        />
        <Route
          path="/newsletter"
          element={
            <Shell>
              <PlaceholderPage
                title="Newsletter"
                description="The BedrockBulletin morning brief — every weekday, in your inbox before the TSX opens. Curated by editors who cover the Canadian mining beat full-time."
              />
            </Shell>
          }
        />
      </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              borderRadius: 0,
              border: "1px solid #0B1F33",
              background: "#ffffff",
              color: "#0B1F33",
              fontFamily: "IBM Plex Sans, sans-serif",
            },
          }}
        />
        <AppRouter />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
